import { useState } from 'react';

import type { CarouselRunCardRow } from '../types/api';

interface Props {
  /** Instagram cards for one format, in carousel order (see `cardsForFormat`). */
  cards: CarouselRunCardRow[];
  /** The caption to preview — the generated caption or the admin's edit. */
  caption: string | null;
  /** Handle of the account this run publishes through, or null. */
  handle: string | null;
}

/**
 * A mock of the Instagram post a run will publish — account header, swipeable
 * carousel of the real rendered cards, and the generated caption beneath. This
 * is the publish page's "see exactly what goes out before you send it" surface.
 */
export function InstagramPostPreview({ cards, caption, handle }: Props) {
  const [idx, setIdx] = useState(0);

  if (cards.length === 0) {
    return (
      <div className="ig-post ig-post-empty muted">
        No Instagram cards to preview.
      </div>
    );
  }

  const safeIdx = Math.min(idx, cards.length - 1);
  const card = cards[safeIdx];
  const accountName = handle ?? 'not connected';
  const captionText = caption && caption.trim() ? caption : null;
  const multi = cards.length > 1;

  return (
    <div className="ig-post">
      <div className="ig-post-head">
        <span className="ig-avatar" aria-hidden="true">
          {(handle ?? 'N').charAt(0).toUpperCase()}
        </span>
        <span className="ig-handle">{accountName}</span>
        <span className="ig-more" aria-hidden="true">
          •••
        </span>
      </div>

      <div
        className="ig-post-media"
        style={{ aspectRatio: `${card.width} / ${card.height}` }}
      >
        <img
          className="ig-post-img"
          src={card.gcs_url}
          alt={`Card ${safeIdx + 1} of ${cards.length}`}
        />
        {multi ? (
          <>
            <button
              type="button"
              className="ig-nav ig-nav-prev"
              aria-label="Previous card"
              disabled={safeIdx === 0}
              onClick={() => setIdx(safeIdx - 1)}
            >
              ‹
            </button>
            <button
              type="button"
              className="ig-nav ig-nav-next"
              aria-label="Next card"
              disabled={safeIdx === cards.length - 1}
              onClick={() => setIdx(safeIdx + 1)}
            >
              ›
            </button>
            <span className="ig-count">
              {safeIdx + 1}/{cards.length}
            </span>
          </>
        ) : null}
      </div>

      {multi ? (
        <div className="ig-dots">
          {cards.map((c, i) => (
            <span
              key={c.id}
              className={i === safeIdx ? 'ig-dot ig-dot-active' : 'ig-dot'}
              aria-hidden="true"
            />
          ))}
        </div>
      ) : null}

      <div className="ig-post-caption">
        {captionText ? (
          <>
            <span className="ig-caption-handle">{accountName}</span>
            <span className="ig-caption-text">{captionText}</span>
          </>
        ) : (
          <span className="ig-caption-empty muted">
            No caption — re-render this carousel.
          </span>
        )}
      </div>
    </div>
  );
}
